import type { ReactNode } from "react";
import { severityClass, severityMeta } from "../lib/labels.js";

export const brl = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// Moeda compacta p/ KPIs (evita estourar cards estreitos): R$ 2,6 mi.
export const brlCompact = (n: number | null | undefined) =>
  n == null ? "—" : "R$ " + n.toLocaleString("pt-BR", { notation: "compact", maximumFractionDigits: 1 });
export const num = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
// Percentual a partir de uma fração (0..1+). Ex.: 0.83 → "83%".
export const pct = (frac: number | null | undefined) =>
  frac == null || Number.isNaN(frac) ? "—" : `${Math.round(frac * 100)}%`;

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card p-5 ${className}`}>{children}</div>;
}

export function Kpi({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </Card>
  );
}

export function Spinner({ label = "Carregando…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
    ?? (error as Error)?.message ?? "Erro";
  return <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{msg}</div>;
}

const STATUS_COLORS: Record<string, string> = {
  LIBERADO: "bg-blue-100 text-blue-700",
  AGENDADO: "bg-amber-100 text-amber-700",
  CARREGADO: "bg-green-100 text-green-700",
  Atraso: "bg-red-100 text-red-700",
  OK: "bg-green-100 text-green-700",
  PBTC: "bg-red-100 text-red-700",
  "✔": "bg-green-100 text-green-700",
};
export function Badge({ children }: { children: string }) {
  const cls = STATUS_COLORS[children] ?? "bg-slate-100 text-slate-600";
  return <span className={`badge ${cls}`}>{children}</span>;
}

/** Badge de severidade (erro/aviso/info) com rótulo pt-BR e ícone. */
export function SeverityBadge({ value }: { value: string }) {
  const m = severityMeta(value);
  return <span className={`badge ${severityClass(value)}`}>{m.icon} {m.label}</span>;
}

export function Table({ columns, rows, stickyFirst = false, maxHeightClass }: {
  columns: { key: string; label: string; render?: (v: unknown, row: Record<string, unknown>) => ReactNode }[];
  rows: Record<string, unknown>[];
  /** Fixa a 1ª coluna ao rolar na horizontal — em grades largas (Operações)
   *  o nº da ordem saía da tela e a linha ficava sem âncora. */
  stickyFirst?: boolean;
  /** Altura máxima (classe Tailwind, ex.: "max-h-[60vh]"). Com ela a tabela
   *  rola por dentro e o CABEÇALHO GRUDA no topo. Sem ela, uma aba com 500
   *  cargas empurra o resto da página para 6 mil pixels abaixo e o operador
   *  perde a referência das colunas na primeira rolagem. */
  maxHeightClass?: string;
}) {
  // Só a 1ª coluna de uma tabela "sticky" ganha fundo próprio — sem fundo, o
  // conteúdo rolado por baixo apareceria através dela.
  //
  // Camadas do z-index quando há rolagem nos DOIS eixos: o canto (linha 0 ×
  // coluna 0) precisa ficar acima do cabeçalho, que fica acima da 1ª coluna,
  // que fica acima do corpo. Empatar deixa o texto rolado aparecendo por cima.
  const stickyTop = maxHeightClass ? "sticky top-0 " : "";
  const thCls = (i: number) =>
    stickyFirst && i === 0
      ? `${stickyTop}sticky left-0 z-30 bg-slate-50 px-4 py-3 font-semibold`
      : `${stickyTop}${maxHeightClass ? "z-20 " : ""}bg-slate-50 px-4 py-3 font-semibold`;
  const tdCls = (i: number) =>
    stickyFirst && i === 0
      ? "sticky left-0 z-10 bg-white px-4 py-2.5 text-slate-700 group-hover:bg-slate-50"
      : "px-4 py-2.5 text-slate-700";
  return (
    <div className={`overflow-x-auto rounded-xl border border-slate-200 bg-white ${maxHeightClass ? `overflow-y-auto ${maxHeightClass}` : ""}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            {columns.map((c, i) => (
              <th key={c.key} className={thCls(i)}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-400">Sem registros.</td></tr>
          )}
          {rows.map((row, i) => (
            <tr key={i} className="group border-b border-slate-100 last:border-0 hover:bg-slate-50">
              {columns.map((c, ci) => (
                <td key={c.key} className={tdCls(ci)}>
                  {c.render ? c.render(row[c.key], row) : String(row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
